/**
 * Раздел, который грузится отдельным куском, — и что делать, когда кусок
 * не приехал.
 *
 * Куски называются по содержимому, и каждая выкладка даёт им новые
 * имена. Вкладка, открытая до выкладки, помнит старые: переходит на
 * «Главную» — и просит файл, которого на сервере уже нет. Без перехвата
 * React оставался без раздела, и человек видел серый экран, пока сам не
 * обновит страницу.
 *
 * Лечится тем же, что человек и делал руками: перезагрузкой. Страница
 * возьмёт свежий `index.html`, а с ним и новые имена. Но только один
 * раз за полминуты: если файл не грузится и после перезагрузки, дело не
 * в выкладке, и крутить страницу по кругу нельзя — тогда ошибку ловит
 * оболочка и показывает кнопку.
 */

import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

const RELOAD_KEY = 'specter.reload-at';
const RELOAD_GAP_MS = 30_000;

/**
 * Перезагрузить страницу, если недавно этого не делали.
 *
 * Возвращает `true`, если перезагрузка пошла. Отметка хранится в
 * `sessionStorage`: она переживает саму перезагрузку, но не уходит в
 * другие вкладки.
 */
export function reloadOnce(
  now: number = Date.now(),
  storage: Pick<Storage, 'getItem' | 'setItem'> | undefined = globalThis.sessionStorage,
  reload: () => void = () => globalThis.location.reload(),
): boolean {
  let last = 0;
  try {
    last = Number(storage?.getItem(RELOAD_KEY) ?? 0) || 0;
  } catch {
    // Хранилище закрыто (приватный режим) — считаем, что не пробовали.
  }
  if (now - last < RELOAD_GAP_MS) return false;

  try {
    storage?.setItem(RELOAD_KEY, String(now));
  } catch {
    // Не запомнили — не беда: худшее, что будет, — лишняя перезагрузка.
  }
  reload();
  return true;
}

/** `lazy`, который при недоехавшем куске перезагружает страницу. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyPage<T extends ComponentType<any>>(
  load: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() =>
    load().catch((cause: unknown) => {
      reloadOnce();
      throw cause;
    }),
  );
}

/**
 * Перехват предзагрузки.
 *
 * Сборщик заранее подтягивает куски, от которых зависит раздел, и о
 * неудаче сообщает своим событием. Без обработчика оно превращается в
 * ту же пустоту.
 */
export function installChunkRecovery(): void {
  globalThis.addEventListener?.('vite:preloadError', (event: Event) => {
    if (reloadOnce()) event.preventDefault();
  });
}
